import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Job } from '@libs/graphql-types';

const useStyles = makeStyles((theme: any) => ({
  baseChip: {
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    position: 'relative',
    fontWeight: 500,
    height: 28,
    borderRadius: 4,
    padding: theme.spacing(0, 1.5),
    textTransform: 'none',
    '& .MuiChip-label': {
      padding: theme.spacing(0, 0.5),
    },
  },
  unknownChip: {
    backgroundColor: '#9e9e9e', // gray
  },
  draftChip: {
    backgroundColor: '#9e9e9e', // gray
  },
  openChip: {
    backgroundColor: '#00c853', // green
  },
  onHoldChip: {
    backgroundColor: '#2196f3', // blue
  },
  chosenChip: {
    backgroundColor: '#9c27b0', // purple
  },
  closedChip: {
    backgroundColor: '#ff9800', // orange
  },
  cancelledChip: {
    backgroundColor: '#f44336', // red
  },
  deletedChip: {
    backgroundColor: '#f44336', // red
  },
}));

interface USJobStatusProps {
  job: Job;
  className?: string;
}

// Status mapping provided by user
const STATUS_MAP = {
  0: 'unknown',
  1: 'draft',
  2: 'open',
  3: 'on_hold',
  4: 'chosen',
  5: 'closed',
  6: 'cancelled',
  7: 'deleted',
} as const;

const USJobStatus: React.FC<USJobStatusProps> = ({ job, className }) => {
  const classes = useStyles();
  const { t } = useTranslation('job');
  const statusValue = job?.striive_job?.job_status;

  // If no status is available, don't render anything
  if (statusValue === null || statusValue === undefined) {
    return null;
  }

  const statusKey = STATUS_MAP[statusValue as keyof typeof STATUS_MAP];

  // Human-readable status labels via i18n
  const STATUS_LABELS: Record<string, string> = {
    unknown: t('v2.jobStatus.unknown'),
    draft: t('v2.jobStatus.draft'),
    open: t('v2.jobStatus.open'),
    on_hold: t('v2.jobStatus.onHold'),
    chosen: t('v2.jobStatus.chosen'),
    closed: t('v2.jobStatus.closed'),
    cancelled: t('v2.jobStatus.cancelled'),
    deleted: t('v2.jobStatus.deleted'),
  };

  const statusLabel = statusKey
    ? STATUS_LABELS[statusKey] || t('v2.jobStatus.unknown')
    : t('v2.jobStatus.unknown');

  // Get the appropriate CSS class for the status
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'unknown':
        return classes.unknownChip;
      case 'draft':
        return classes.draftChip;
      case 'open':
        return classes.openChip;
      case 'on_hold':
        return classes.onHoldChip;
      case 'chosen':
        return classes.chosenChip;
      case 'closed':
        return classes.closedChip;
      case 'cancelled':
        return classes.cancelledChip;
      case 'deleted':
        return classes.deletedChip;
      default:
        return classes.unknownChip;
    }
  };

  return (
    <Chip
      label={statusLabel}
      size="small"
      className={classNames(
        classes.baseChip,
        getStatusClass(statusKey),
        className,
      )}
    />
  );
};

export default USJobStatus;
